import { useState, useEffect, useCallback, useRef } from 'react';
import { usePusherContext } from '../lib/context/PusherContext';
import { stockReviewService } from '../lib/services/stockReviewService';
import type {
  StockReviewDecision,
  StockReviewWithMatches,
} from '../types/stock';

export type { StockReviewWithMatches } from '../types/stock';

export const useStockReview = (batchId: string, clientId: string) => {
  const [pendingReviews, setPendingReviews] = useState<
    StockReviewWithMatches[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { pusher, connect, disconnect } = usePusherContext();
  const isFetchingRef = useRef(false);

  const currentRequest = pendingReviews[currentIndex] || null;
  const totalReviews = pendingReviews.length;
  const currentReviewNumber = currentIndex + 1;
  const hasMoreReviews = currentIndex < pendingReviews.length - 1;

  const paginationInfo = {
    currentPage: currentRequest?.currentPage ?? currentReviewNumber,
    totalItems: currentRequest?.totalItems ?? totalReviews,
    pageSize: currentRequest?.pageSize ?? 1,
  };

  const fetchPendingReviews = useCallback(async () => {
    if (!clientId || !batchId || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const reviews = await stockReviewService.getRequiredReviews(
        clientId,
        batchId,
      );
      setPendingReviews(reviews);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to fetch pending stock reviews:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [clientId, batchId]);

  useEffect(() => {
    if (!batchId) return;
    connect();
    return () => {
      disconnect();
    };
  }, [batchId, connect, disconnect]);

  useEffect(() => {
    if (!pusher || !batchId || !clientId) return;

    const channelName = `stock-review-required-${batchId}`;
    const channel = pusher.subscribe(channelName);

    channel.bind('stock-review-required', () => {
      fetchPendingReviews();
    });

    channel.bind('stock-review-updated', () => {
      fetchPendingReviews();
    });

    return () => {
      channel.unbind('stock-review-required');
      channel.unbind('stock-review-updated');
      pusher.unsubscribe(channelName);
    };
  }, [pusher, batchId, clientId, fetchPendingReviews]);

  const submitDecision = useCallback(
    async (decision: StockReviewDecision): Promise<void> => {
      await stockReviewService.submitDecision(clientId, decision);

      if (currentIndex < pendingReviews.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setPendingReviews([]);
        setCurrentIndex(0);
      }
    },
    [clientId, currentIndex, pendingReviews.length],
  );

  const closeModal = useCallback(() => {
    setPendingReviews([]);
    setCurrentIndex(0);
  }, []);

  return {
    currentRequest,
    submitDecision,
    closeModal,
    totalReviews,
    currentReviewNumber,
    hasMoreReviews,
    paginationInfo,
    isLoading,
    fetchPendingReviews,
  };
};

export default useStockReview;
